import { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RadioGroup, Stack, Typography } from '@mui/material';

import { CancellationReasonPassengerBookingEnum } from '@mopla/data-models';
import { MplTrans } from '@mopla/i18';
import { useBreakpoints } from '@mopla/utils';

import {
	IconMessage,
	InfoBaseIcon,
	Input,
	MplButton,
	MplPaper,
	Radio,
} from '../../../';

import { ReasonFormStepWrapper } from './ReasonFormStepWrapper';
import { ICancellationReasonFormState } from './types';
import { MAX_OTHER_REASON_INPUT } from './useCancellationReasonForm';

export interface IReasonStepProps {
	isOpen: boolean;
	onCancel: VoidFunction;
	onSubmit: VoidFunction;
	cancellationPrice: 0 | string | null;
	isPRODT: boolean;
	withDelayReason?: boolean;
}

const cancellationReasonsListBase: CancellationReasonPassengerBookingEnum[] = [
	CancellationReasonPassengerBookingEnum.RECEIVING_CHEAPER,
	CancellationReasonPassengerBookingEnum.NOT_NEEDED_ANYMORE,
	CancellationReasonPassengerBookingEnum.OTHER_REASON,
];

export const ReasonFormStep: FC<IReasonStepProps> = ({
	isOpen,
	onCancel,
	onSubmit,
	cancellationPrice,
	isPRODT,
	withDelayReason,
}) => {
	const { t } = useTranslation('tickets');
	const {
		watch,
		control,
		setValue,
		formState: { isValid, isSubmitting },
	} = useFormContext<ICancellationReasonFormState>();
	const [reasonValue] = watch(['reason', 'otherReasonText']);
	const { isMobile } = useBreakpoints();

	useEffect(() => {
		if (reasonValue !== CancellationReasonPassengerBookingEnum.OTHER_REASON) {
			setValue('otherReasonText', '');
		}
	}, [reasonValue, setValue]);

	const cancellationReasonsList = useMemo(
		() => [
			...(withDelayReason
				? [CancellationReasonPassengerBookingEnum.DELAY]
				: []),
			...cancellationReasonsListBase,
		],
		[withDelayReason]
	);

	return (
		<ReasonFormStepWrapper
			isOpen={isOpen}
			onCancel={onCancel}
			buttonsBlock={
				<MplButton
					fullWidth
					color={isMobile ? 'primaryRevert' : 'primary'}
					onClick={onSubmit}
					disabled={isSubmitting || !isValid}
					showLoader={isSubmitting}
					data-testid="BookingCancellationFlow-ReasonsStep-confirmButton"
				>
					{t('button.cancel', { context: 'ride' })}
				</MplButton>
			}
		>
			<MplPaper
				gap={3}
				p={{ tabletP: 0 }}
				sx={{ '.force-mobile &': { p: '24px 16px' } }}
			>
				<Typography variant="bodyTitle">
					{t('cancellation_flow.reason.title')}
				</Typography>
				<Controller
					name="reason"
					control={control}
					render={({ field }) => (
						<RadioGroup {...field}>
							{cancellationReasonsList.map((reason) => (
								<Radio
									key={reason}
									label={t(`cancellation_reasons_passenger.${reason}`)}
									name="reason"
									value={reason}
									disabled={isSubmitting}
									labelTestId={`BookingCancellationFlow-ReasonsStep-${reason}-label`}
								/>
							))}
						</RadioGroup>
					)}
				/>
				{reasonValue ===
					CancellationReasonPassengerBookingEnum.OTHER_REASON && (
					<Controller
						name="otherReasonText"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								ref={null}
								disabled={isSubmitting}
								inputProps={{
									maxLength: MAX_OTHER_REASON_INPUT,
									'data-testid': `BookingCancellationFlow-ReasonsStep-${CancellationReasonPassengerBookingEnum.OTHER_REASON}-input`,
								}}
								helperText={`${
									field.value?.length || 0
								}/${MAX_OTHER_REASON_INPUT}`}
							/>
						)}
					/>
				)}
			</MplPaper>
			<MplPaper
				gap={3}
				p={{ tabletP: 0 }}
				sx={{ '.force-mobile &': { p: '24px 16px' } }}
			>
				<Stack gap={1.5}>
					<Typography variant="body1" fontWeight={600}>
						<MplTrans
							ns="tickets"
							i18nKey="cancellation_flow.confirmation.body"
							context={cancellationPrice === 0 ? 'free' : undefined}
							values={{ cost: cancellationPrice }}
						/>
					</Typography>
					{cancellationPrice !== 0 && (
						<IconMessage
							color="neutral.80"
							icon={<InfoBaseIcon />}
							message={t('cancellation_flow.reason.description')}
						/>
					)}
				</Stack>
				{!isPRODT && (
					<Stack gap={1.5}>
						<Typography variant="body2">
							<MplTrans ns="tickets" i18nKey="text.note_message" />
						</Typography>
						<IconMessage
							color="neutral.80"
							icon={<InfoBaseIcon />}
							message={t('text.info_message')}
						/>
					</Stack>
				)}
			</MplPaper>
		</ReasonFormStepWrapper>
	);
};
